import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ImageTile from 'Compass/profile-lab/components/ImageTile';
import { Photo } from 'Compass/profile-lab/types';
import { db } from 'Compass/profile-lab/services/db';
import { attachPhotoStatus } from 'Compass/profile-lab/utils/assetFormatting';
import { getUserLid } from 'Compass/profile-lab/hooks/useUserLid';
import { getSelectionSets } from 'Compass/profile-lab/utils/selectionUtils';
import { fetchSignedUrlsForCurrentUser } from 'Compass/profile-lab/hooks/useSignedUrls';
import { apiPost } from 'Compass/profile-lab/utils/apiClient';
import { ErrorMessage, LoadingMessage } from 'Compass/profile-lab/styles/common';
import { PeopleLink } from 'Compass/profile-lab/styles/people';

interface PersonDataWithCounts {
  person_id: number;
  photo_count: number;
  first_photo: Photo;
  grow_count: number;
  selected_count: number;
  total_count: number;
}

const ProfileLabPeoplePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const personId = searchParams.get('id');

  const [people, setPeople] = useState<PersonDataWithCounts[]>([]);
  const [assets, setAssets] = useState<Photo[]>([]);
  const [signedUrls, setSignedUrls] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await db.init();
        const userLid = await getUserLid();

        if (personId) {
          const result = await apiPost<{ photos: Photo[] }>('/api/dazzle/profile-lab/people/photos', {
            user_lid: userLid,
            person_id: parseInt(personId, 10),
          });

          const data: Photo[] = result.photos;
          const { selectedSet, growSet } = await getSelectionSets();

          const photosWithStatus = attachPhotoStatus(data, selectedSet, growSet);
          setAssets(photosWithStatus);

          const photoLids = data.map(a => a.photo_lid);
          const urls = await fetchSignedUrlsForCurrentUser(photoLids);
          setSignedUrls(urls);
        } else {
          const { selectedSet, growSet, selectionState } = await getSelectionSets();

          const result = await apiPost<{ people: PersonDataWithCounts[] }>('/api/dazzle/profile-lab/people/list', {
            user_lid: userLid,
            grow_lids: selectionState.grow,
            selected_lids: selectionState.selected,
          });

          const data: PersonDataWithCounts[] = result.people;

          const peopleWithStatus = data.map((person: PersonDataWithCounts) => {
            const personAsset: Photo = {
              ...person.first_photo,
              status:
                selectedSet.has(person.first_photo.photo_lid) ? 'selected' as const :
                growSet.has(person.first_photo.photo_lid) ? 'grow' as const :
                'none' as const,
            };
            return {
              person_id: person.person_id,
              photo_count: person.photo_count,
              first_photo: personAsset,
              grow_count: person.grow_count,
              selected_count: person.selected_count,
              total_count: person.total_count,
            };
          });

          setPeople(peopleWithStatus);

          const firstPhotoLids = peopleWithStatus.map(p => p.first_photo.photo_lid);
          const urls = await fetchSignedUrlsForCurrentUser(firstPhotoLids);
          setSignedUrls(urls);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load people data';
        setError(errorMessage);
        console.error('Failed to fetch people data:', err);
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [personId]);

  if (loading) {
    return <LoadingMessage>Loading...</LoadingMessage>;
  }

  if (error) {
    return <ErrorMessage>Error: {error}</ErrorMessage>;
  }

  if (personId) {
    return (
      <>
        {assets.map((asset) => (
          <ImageTile key={asset.photo_lid} asset={asset} imageUrl={signedUrls[asset.photo_lid]} />
        ))}
      </>
    );
  }

  return (
    <>
      {people.map((person) => (
        <ImageTile
          key={person.person_id}
          asset={person.first_photo}
          imageUrl={signedUrls[person.first_photo.photo_lid]}
          textLines={[
            <PeopleLink
              to={`/profile-lab/people?id=${person.person_id}`}
              key={person.person_id}
            >
              Show ({person.grow_count}/{person.selected_count}/{person.total_count})
            </PeopleLink> as unknown as string
          ]}
        />
      ))}
    </>
  );
};

export default ProfileLabPeoplePage;
